import { useCallback, useMemo } from "react";

import { useEvents } from "@/graphql/hooks/useEvents";
import { useDebounce } from "@/hooks/useDebounce";
import {
  selectCurrentEvent,
  selectEventContents,
  selectEventError,
  selectEventFilters,
  selectEventLoading,
  selectHasActiveFilters,
  selectPlatformEventMembers,
  selectSelectedEventIds,
} from "@/lib/redux/selectors";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  type EventFilters,
  resetFilters,
  setFilters,
  setOpenEvent,
  setCurrentEvent,
  clearCurrentEvent,
  setPlatformEventMembers,
  clearPlatformEventMembers,
  setEventContents,
  clearEventContents,
  setLoading,
  setError,
} from "@/store/slices/eventsSlice";

/**
 * Aggregate feature hook for the Events domain.
 *
 * Combines:
 *   - Redux filter/selection state
 *   - Debounced search → Apollo query
 *   - Derived UI flags (hasActiveFilters, hasLiveEvents)
 *
 * Pages import this single hook instead of calling Redux + Apollo separately.
 * This keeps pages thin and makes the feature logic independently testable.
 */
export function useEventsFeature() {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectEventFilters);
  const selectedIds = useAppSelector(selectSelectedEventIds);
  const hasActiveFilters = useAppSelector(selectHasActiveFilters);

  // Debounce the search string so we don't hit Apollo on every keystroke
  const debouncedSearch = useDebounce(filters.search, 300);

  const activeFilters: Partial<EventFilters> = useMemo(
    () => ({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch],
  );

  // Live events always poll; others use cache-first
  const isLiveFilter = filters.status === "live";

  const { events, pageInfo, loading, error, loadMore, refetch } = useEvents({
    filters: activeFilters,
    pollLive: isLiveFilter,
  });


  // ─── Actions ──────────────────────────────────────────────────
  const updateFilters = useCallback(
    (patch: Partial<EventFilters>) => dispatch(setFilters(patch)),
    [dispatch],
  );

  const clearFilters = useCallback(() => dispatch(resetFilters()), [dispatch]);

  const openEvent = useCallback(
    (id: string | null) => dispatch(setOpenEvent(id)),
    [dispatch],
  );

  const currentEvent = useAppSelector(selectCurrentEvent);

  const openCurrentEvent = useCallback(
    (event: any) => dispatch(setCurrentEvent(event)),
    [dispatch],
  );

  const closeCurrentEvent = useCallback(
    () => dispatch(clearCurrentEvent()),
    [dispatch],
  );

  const platformEventMembers = useAppSelector(selectPlatformEventMembers);

  const openPlatformEventMembers = useCallback(
    (event: any) => dispatch(setPlatformEventMembers(event)),
    [dispatch],
  );

  const closePlatformEventMembers = useCallback(
    () => dispatch(clearPlatformEventMembers()),
    [dispatch],
  );

  const eventContents = useAppSelector(selectEventContents);

  const openEventContents = useCallback(
    (event: any) => dispatch(setEventContents(event)),
    [dispatch],
  );

  const closeEventContents = useCallback(
    () => dispatch(clearEventContents()),
    [dispatch],
  );

  const eventLoading = useAppSelector(selectEventLoading);

  const setEventLoading = useCallback(
    (loading: any) => dispatch(setLoading(loading)),
    [dispatch],
  );

  const eventError = useAppSelector(selectEventError);

  const setEventError = useCallback(
    (error: any) => dispatch(setError(error)),
    [dispatch],
  );

  return {
    // Data
    events,
    pageInfo,
    loading,
    error,
    loadMore,
    refetch,
    // Filters
    filters,
    hasActiveFilters,
    updateFilters,
    clearFilters,
    // Selection
    selectedIds,
    // Detail panel
    openEvent,
    // Derived
    // hasLiveEvents,
    currentEvent,
    openCurrentEvent,
    closeCurrentEvent,
    platformEventMembers,
    openPlatformEventMembers,
    closePlatformEventMembers,
    eventContents,
    openEventContents,
    closeEventContents,
    eventLoading,
    setEventLoading,
    eventError,
    setEventError,
  };
}
